import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { Meta } from '@angular/platform-browser';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {

  public customThemeChangeSubject: BehaviorSubject<string> = new BehaviorSubject<string>(undefined);
  public themeChangeSubject: BehaviorSubject<string> = new BehaviorSubject<string>(undefined);
  public webAccessibilitySubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(undefined);
  public videoBackgroundDisabled: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(undefined);

  public themeAssetSourceChanged: Subject<string> = new Subject<string>();

  public langTheme: string;

  protected currentCustomTheme: string;
  protected currentTheme: string;
  protected currentApplicationCustomAssetDir: string;
  protected currentApplicationAssetDir: string;
  private renderer: Renderer2;

  /**
   * Constructor
   */
  public constructor(
    @Inject(DOCUMENT) private document,
    private rendererFactory: RendererFactory2,
    private meta: Meta) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  /**
   * Get the theme
   *
   * @return string
   */
  public get theme(): string {
    return this.currentTheme;
  }

  /**
   * Set the theme
   *
   * @param theme
   */
  public set theme(theme: string) {
    if (theme !== this.theme) {
      this.currentTheme = theme;
      this.langTheme = theme;
      this.themeChangeSubject.next(theme);
    }
  }

  /**
   * Get the theme
   *
   * @return string
   */
  public get customTheme(): string {
    return this.currentCustomTheme;
  }

  /**
   * Set the theme
   *
   * @param customTheme
   *
   * @return void
   */
  public set customTheme(customTheme: string) {
    if (customTheme !== this.currentCustomTheme) {
      this.currentCustomTheme = customTheme;
    }
  }

  /**
   * Get the assets directory
   *
   * @return {string}
   */
  public get assetsDir(): string {
    return this.currentApplicationAssetDir;
  }

  /**
   * Set the assets directory
   *
   * @param {string} dir
   */
  public set assetsDir(dir: string) {
    this.currentApplicationAssetDir = dir;
  }

  /**
   * Get the assets directory
   *
   * @return {string}
   */
  public get customAssetsDir(): string {
    return this.currentApplicationCustomAssetDir;
  }

  /**
   * Set the assets directory
   *
   * @param {string} dir
   */
  public set customAssetsDir(dir: string) {
    this.currentApplicationCustomAssetDir = dir;
  }

  /**
   * Set the Theme
   *
   * @param {string} theme
   */
  public setTheme(theme: string): void {
    if (theme && theme !== this.theme) {
      this.theme = theme;
    }
  }

  /**
   * Listener for when the theme change
   *
   * @return {Observable<string>}
   */
  public getThemeChange(): Observable<string> {
    return this.themeChangeSubject.asObservable();
  }

  /**
   * Listener for when the custom theme changes
   *
   * @return {Observable<string>}
   */
  public getCustomThemeChange(): Observable<string> {
    return this.customThemeChangeSubject.asObservable();
  }

  /**
   * Listener when the webAccessibility has been set
   *
   * @return {Observable<boolean>}
   */
  public getWebAccessibilityChange(): Observable<boolean> {
    return this.webAccessibilitySubject.asObservable();
  }

  /**
   * Listener for when the theme assets source has changed
   *
   * @return {Observable<string>}
   */
  public getThemeAssetsSourceChanged(): Observable<string> {
    return this.themeAssetSourceChanged.asObservable();

  }

  /**
   * Creates the stylesheet link in the HEAD element
   *
   * @param customTheme
   *
   * @return Promise<boolean>
   */
  public async createStyleLinkInHead(customTheme: string): Promise<void> {

    document.body.classList.add('custom-theme-' + customTheme);

    const metaTagCustomTheme: HTMLMetaElement = this.meta.getTag('name=application-custom-theme-assets-dir');
    if (metaTagCustomTheme) {

      let customThemeStyleSheetElement: HTMLElement = document.getElementById('theme-stylesheet-custom');

      // Check if the link element already exists. If not, create it.
      if (!customThemeStyleSheetElement) {
        customThemeStyleSheetElement = this.renderer.createElement('link');
        this.renderer.setAttribute(customThemeStyleSheetElement, 'id', 'theme-stylesheet-custom');
        this.renderer.setAttribute(customThemeStyleSheetElement, 'rel', 'stylesheet');
        this.renderer.appendChild(document.head, customThemeStyleSheetElement);
      }

      // Update values on the element in case they do not match at the moment
      this.renderer.setAttribute(customThemeStyleSheetElement, 'href', `${metaTagCustomTheme.content}${customTheme}/shared/css/custom.css`);
      this.renderer.setAttribute(customThemeStyleSheetElement, 'data-theme', customTheme);
    }
  }

  /**
   * Removes the stylesheet link from the HEAD element
   *
   * @param customTheme
   *
   * @return Promise<boolean>
   */
  public async removeStyleLinkInHead(customTheme: string): Promise<void> {

    customTheme = this.customTheme;
    document.body.classList.remove('custom-theme-' + customTheme);

    const customThemeStyleSheetElement: HTMLElement = document.getElementById('theme-stylesheet-custom');
    if (customThemeStyleSheetElement) {
      this.renderer.removeChild(document.head, customThemeStyleSheetElement);
    }
  }

}
